import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SettingsOpsService } from '../settings-ops/settings-ops.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-switch-user',
    templateUrl: './switch-user.component.html',
    styleUrls: ['./switch-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SwitchUserComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      this.baseURLs = safeJsonInput<any>(v || '{}', null, 'switch-user.parse1');
    } catch (_) {}
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = safeJsonInput<any>(v || '{}', null, 'switch-user.parse2');
      this.GENERAL_CONFIG = header?.generalConfigData || {};
      this.USER_SETTINGS = header?.userSettingsData || {};
    } catch (_) {}
  }

  @Input('row-data')
  set _row_data(v: string) {
    try {
      this.rowData = safeJsonInput<any>(v || '{}', null, 'switch-user.parse3');
    } catch (_) {}
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      this.filterParams = safeJsonInput<any>(v || '{}', null, 'switch-user.parse4');
    } catch (_) {}
  }

  baseURLs: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  rowData: any = {};
  filterParams: any = {};

  selectedUser = '';
  loading = false;
  noRecords = false;
  showList = false;
  switchReset = true;
  userDetailsArr: string[] = [];

  constructor(
    private settingsOpsService: SettingsOpsService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
  }

  private get adminApiUrl(): string {
    return this.GENERAL_CONFIG?.admin_api_url || this.baseURLs?.admin || '';
  }

  getUsers(): void {
    const search = (this.selectedUser || '').trim();
    if (!search || !this.baseUrl) {
      this.userDetailsArr = [];
      this.noRecords = false;
      this.showList = false;
      this.cdr.detectChanges();
      return;
    }
    this.loading = true;
    this.noRecords = false;
    this.userDetailsArr = [];
    this.settingsOpsService.getUserDetails(this.baseUrl, search).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        const users = data?.userDetails || [];
        this.userDetailsArr = users.map((u: any) => `${u.first_name} ${u.last_name} - ${u.sso_id}`);
        this.loading = false;
        this.showList = this.userDetailsArr.length > 0;
        this.noRecords = this.userDetailsArr.length === 0;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.showList = false;
        this.noRecords = true;
        this.cdr.detectChanges();
      },
    });
  }

  fillTextbox(userData: string): void {
    this.selectedUser = userData;
    this.showList = false;
    this.switchReset = false;
    this.cdr.detectChanges();
  }

  reset(): void {
    this.selectedUser = '';
    this.userDetailsArr = [];
    this.switchReset = true;
    this.noRecords = false;
    this.showList = false;
    this.cdr.detectChanges();
  }

  switchUser(): void {
    const selected = this.selectedUser || '';
    const sso = selected.split('- ').pop() || '';
    if (!sso || !this.adminApiUrl) {
      this.gtwSnackbarService.openSnackBar('Please select a user', 'gtw-snackbar--yellow');
      return;
    }
    this.loading = true;
    this.settingsOpsService.changeSso(this.adminApiUrl, sso).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.loading = false;
        if (String(data?.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('User switched successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.message || 'Unable to switch user', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.gtwSnackbarService.openSnackBar('Error while switching user', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
